import * as React from 'react'

/**
 * Drop-in replacement for useState that mirrors the value to localStorage.
 *
 * Used by the Create-Ad wizard so a user's in-progress idea/setup survives
 * back-navigation between steps AND a browser refresh. When the user
 * successfully generates an ad (or explicitly clears the draft), call
 * clearCreateAdDraft() below to wipe every namespaced key in one shot.
 *
 * Behaviour:
 *   - On mount, reads `key` from localStorage. If present and parseable,
 *     uses that as the initial value. Otherwise falls back to `initial`.
 *   - On every state change, serializes via JSON.stringify and writes back.
 *   - Silently no-ops if localStorage is unavailable (private browsing,
 *     SSR, quota exceeded). Falls back to in-memory state in that case.
 *
 * Caller contract:
 *   - Keep `key` stable across renders. Changing it mid-lifecycle won't
 *     migrate the existing value — write a separate effect for that.
 *   - The value must be JSON-serializable. For Asset[] etc, store IDs and
 *     rehydrate the full objects via API on mount in the consuming component.
 */
export function useLocalStorageState<T>(
  key: string,
  initial: T,
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [value, setValue] = React.useState<T>(() => {
    if (typeof window === 'undefined') return initial
    try {
      const raw = window.localStorage.getItem(key)
      if (raw == null) return initial
      return JSON.parse(raw) as T
    } catch {
      return initial
    }
  })

  React.useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(key, JSON.stringify(value))
    } catch {
      /* quota / private browsing — falls back to in-memory only */
    }
  }, [key, value])

  return [value, setValue]
}

/** Prefix every Create-Ad draft key with this so clearCreateAdDraft() can
 *  wipe them in one pass without touching unrelated localStorage. */
export const CREATE_AD_DRAFT_PREFIX = 'aioverlords.createAd.'

/** Clear every persisted draft field for the Create-Ad wizard. Call this
 *  after a successful assembly job is created, or from a "Start over"
 *  button. Safe to call when no draft exists. */
export function clearCreateAdDraft(): void {
  if (typeof window === 'undefined') return
  try {
    const keys: string[] = []
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i)
      if (k && k.startsWith(CREATE_AD_DRAFT_PREFIX)) keys.push(k)
    }
    keys.forEach((k) => window.localStorage.removeItem(k))
  } catch {
    /* localStorage unavailable — nothing to clear */
  }
}
